import * as Api from '@ViewModels';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useActionItemStepState } from '../../hooks/useActionItemStepState';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { ActionItemStepForm } from '../../components/ActionItemStepForm';
import { ActionItemAdvancedOptions } from '../../components/ActionItemAdvancedOptions';

interface ActionItemStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function ActionItemStepRenderer({ nodeId, data, readOnly: readOnlyProp }: ActionItemStepRendererProps) {
	const errorMessages = useErrorMessages();

	const { automationStep, automationTemplate, _stepData } = data;
	const actionItemStep = automationStep as Api.IActionItemAutomationStep;
	const template = automationTemplate;

	const canEdit = template?.status !== 'Archived' && !readOnlyProp;
	const readonly = !canEdit || readOnlyProp;

	const {
		actionItemContentEditorState,
		setActionItemContentEditorState,
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
		clearInlineErrorMessage,
		save,
	} = useActionItemStepState({
		actionItemStep,
		template,
		stepId: data.stepId || '',
		readonly,
		onSaveSuccess: () => {
			// Query invalidation happens automatically
		},
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onActionItemContentEditorStateChanged = (newState: Api.IRichContentEditorState) => {
		setActionItemContentEditorState(newState);
		clearInlineErrorMessage();
	};

	const saveSenderOptions = (option: Api.ISendFromOptions) => {
		save({ newSendFrom: option });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
	};

	const hasContent = !!actionItemContentEditorState?.hasContent();
	const baseKey = `${data.stepType}-${nodeId}-${actionItemStep?.id || ''}`;

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			<ActionItemStepForm
				actionItemContentEditorState={actionItemContentEditorState}
				hasContent={hasContent}
				readonly={readonly}
				showRequiredFieldError={showRequiredFieldError}
				baseKey={baseKey}
				onContentStateChanged={onActionItemContentEditorStateChanged}
				onSave={() => save()}
				onClearError={clearInlineErrorMessage}
			/>
			<ActionItemAdvancedOptions
				actionItemStep={actionItemStep}
				nodeId={nodeId}
				stepId={data.stepId || nodeId}
				skipStepContactFilter={skipStepContactFilter}
				canEdit={canEdit}
				isCollapsed={!advancedOptionsToggled}
				onToggle={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
				onSaveSenderOptions={saveSenderOptions}
				onFilterChange={handleFilterChange}
			/>
		</>
	);
}
